import { useAccess } from '../../hooks/useAccess'
import React, { useState, useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ChevronRightIcon, PlusIcon, ChevronLeftIcon, FolderIcon, ClockIcon, CubeIcon, CheckCircleIcon, ExclamationTriangleIcon, MagnifyingGlassIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, CheckIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/Button'
import { jobProjectApi } from '../../services/jobProjectApi'
import { JobProject, JobProjectWithHistory } from '../../types/jobProject'
import { listStatusBadgeClass } from '../../lib/optimizationStyles'
import { getVehicleTypeLabel, getVehicleSubtypeLabel } from '../../lib/vehicleHelpers'
import CreateProjectDialog from '../../components/optimization/CreateProjectDialog'
import { PencilIcon } from '@heroicons/react/24/outline'
import { projectRunSelection } from '../../lib/optimizationReadiness'

const PAGE_SIZE = 10

const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Tous les statuts' },
  { value: 'draft', label: 'Brouillon' },
  { value: 'active', label: 'Actif' },
  { value: 'completed', label: 'Complété' },
  { value: 'archived', label: 'Archivé' },
]

export default function OptimizationList() {
  const access = useAccess()
  const rights = access.resource('optimization')
  const canWrite = rights.write
  const canManage = rights.manage
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<JobProject['status'] | ''>('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false)
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set())
  const [projectHistories, setProjectHistories] = useState<Map<string, JobProjectWithHistory>>(new Map())
  const [collapsedPeriods, setCollapsedPeriods] = useState<Set<string>>(new Set())
  const [loadingMoreRuns, setLoadingMoreRuns] = useState<Set<string>>(new Set())
  const [runPagination, setRunPagination] = useState<Map<string, { limit: number; offset: number; total: number }>>(new Map())
  const [selectedRunIds, setSelectedRuns] = useState<Set<string>>(new Set())
  const [editingProject, setEditingProject] = useState<JobProject | null>(null)
  const [historyErrors, setHistoryErrors] = useState<Map<string, string>>(new Map())
  const [searchQuery, setSearchQuery] = useState('')
  useEffect(() => {
    const timer = window.setTimeout(() => { setSearchQuery(search.trim()); setPage(0) }, 250)
    return () => window.clearTimeout(timer)
  }, [search])

  const RUNS_INITIAL_LIMIT = 20
  const RUNS_LOAD_MORE = 20

  const offset = page * PAGE_SIZE

  useEffect(() => {
    setPage(0)
  }, [statusFilter])


  const { data: response, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ['job-projects', statusFilter, searchQuery, page],
    enabled: rights.read,
    queryFn: () =>
      jobProjectApi.listProjects({
        status: statusFilter || undefined,
        search: searchQuery || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    keepPreviousData: true,
  })

  const projects = rights.read ? response?.projects || [] : []
  const total = response?.total ?? projects.length
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  useEffect(() => { if (!isFetching && page >= totalPages) setPage(totalPages - 1) }, [totalPages, page, isFetching])
  const rangeStart = total === 0 ? 0 : offset + 1
  const rangeEnd = Math.min(offset + projects.length, total)

  const getStatusLabel = useCallback((status: JobProject['status']) => {
    const labels: Record<string, string> = {
      draft: 'Brouillon',
      active: 'Actif',
      completed: 'Complété',
      archived: 'Archivé',
    }
    return labels[status] || status
  }, [])

  const loadProjectHistory = useCallback(async (jobId: string) => {
    setLoadingMoreRuns(prev => new Set(prev).add(jobId))
    setHistoryErrors(prev => { const next = new Map(prev); next.delete(jobId); return next })
    try {
      const history = await jobProjectApi.getProjectRuns(jobId, { limit: RUNS_INITIAL_LIMIT, offset: 0 })
      setProjectHistories(prev => new Map(prev).set(jobId, history))
      setRunPagination(prev => new Map(prev).set(jobId, { limit: RUNS_INITIAL_LIMIT, offset: 0, total: history.total ?? history.runs.length }))
    } catch {
      setHistoryErrors(prev => new Map(prev).set(jobId, 'Historique indisponible. Réessayez.'))
    } finally {
      setLoadingMoreRuns(prev => { const next = new Set(prev); next.delete(jobId); return next })
    }
  }, [])

  const toggleProjectExpansion = useCallback((jobId: string) => {
    const opening = !expandedProjects.has(jobId)
    setExpandedProjects(prev => {
      const next = new Set(prev)
      opening ? next.add(jobId) : next.delete(jobId)
      return next
    })
    if (opening && !projectHistories.has(jobId) && !loadingMoreRuns.has(jobId)) void loadProjectHistory(jobId)
  }, [expandedProjects, projectHistories, loadingMoreRuns, loadProjectHistory])

  const handleDeleteProject = useCallback(async (jobId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!canManage) return
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce projet de chargement ? Cette action est irréversible.')) {
      try {
        await jobProjectApi.deleteProject(jobId)
        await refetch()
        // Refresh the project list
        setExpandedProjects(prev => {
          const next = new Set(prev)
          next.delete(jobId)
          return next
        })
        setProjectHistories(prev => {
          const next = new Map(prev)
          next.delete(jobId)
          return next
        })
      } catch (error) {
        console.error('Erreur lors de la suppression du projet:', error)
        alert('Erreur lors de la suppression du projet')
      }
    }
  }, [canManage, refetch])

  const handleDuplicateProject = useCallback(async (jobId: string, projectName: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!canWrite) return
    const newName = prompt('Nom du projet dupliqué:', `${projectName} (copie)`)
    if (newName) {
      try {
        await jobProjectApi.duplicateProject(jobId, newName)
        // Refresh the project list
        refetch()
      } catch (error) {
        console.error('Erreur lors de la duplication du projet:', error)
        alert('Erreur lors de la duplication du projet')
      }
    }
  }, [refetch, canManage, canWrite])

  const handleDeleteRun = useCallback(async (jobId: string, runId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!canManage) return
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette version du projet ? Cette action est irréversible.')) {
      try {
        await jobProjectApi.deleteRun(jobId, runId)
        // Refresh the project history
        jobProjectApi.getProjectRuns(jobId).then(history => {
          setProjectHistories(prev => new Map(prev).set(jobId, history))
        })
      } catch (error) {
        console.error('Erreur lors de la suppression de la version:', error)
        alert('Erreur lors de la suppression de la version')
      }
    }
  }, [canManage])

  const groupRunsByPeriod = useCallback((runs: any[]) => {
    const groups: Map<string, any[]> = new Map()

    runs.forEach(run => {
      const date = new Date(run.requested_at)
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      let periodKey: string
      let periodLabel: string

      if (date >= today) {
        periodKey = 'today'
        periodLabel = "Aujourd'hui"
      } else if (date >= yesterday) {
        periodKey = 'yesterday'
        periodLabel = 'Hier'
      } else if (date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()) {
        periodKey = `month-${date.getMonth()}`
        periodLabel = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
      } else if (date.getFullYear() === now.getFullYear()) {
        periodKey = `month-${date.getMonth()}-${date.getFullYear()}`
        periodLabel = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
      } else {
        periodKey = `year-${date.getFullYear()}`
        periodLabel = date.getFullYear().toString()
      }

      if (!groups.has(periodKey)) {
        groups.set(periodKey, [])
      }
      groups.get(periodKey)!.push({ ...run, periodLabel })
    })

    // Sort groups by period (most recent first)
    const sortedGroups = Array.from(groups.entries()).sort((a, b) => {
      const dateA = new Date(a[1][0].requested_at)
      const dateB = new Date(b[1][0].requested_at)
      return dateB.getTime() - dateA.getTime()
    })

    // For periods with many runs, group by hour
    const HOUR_THRESHOLD = 5 // Group by hour if more than 5 runs in a period

    return sortedGroups.map(([periodKey, periodRuns]) => {
      if (periodRuns.length > HOUR_THRESHOLD && (periodKey === 'today' || periodKey === 'yesterday')) {
        // Group by hour for today/yesterday with many runs
        const hourGroups: Map<string, any[]> = new Map()

        periodRuns.forEach(run => {
          const date = new Date(run.requested_at)
          const hour = date.getHours()
          const hourKey = `${periodKey}-hour-${hour}`
          const hourLabel = `${hour}h`

          if (!hourGroups.has(hourKey)) {
            hourGroups.set(hourKey, [])
          }
          hourGroups.get(hourKey)!.push({ ...run, hourLabel })
        })

        // Sort hour groups (most recent first)
        const sortedHourGroups = Array.from(hourGroups.entries()).sort((a, b) => {
          const hourA = parseInt(a[0].split('-').pop()!)
          const hourB = parseInt(b[0].split('-').pop()!)
          return hourB - hourA
        })

        return [periodKey, periodRuns, sortedHourGroups] as [string, any[], [string, any[]][]]
      }

      return [periodKey, periodRuns, null] as [string, any[], null]
    })
  }, [])

  const togglePeriodCollapse = useCallback((periodKeyWithProject: string) => {
    setCollapsedPeriods(prev => {
      const next = new Set(prev)
      if (next.has(periodKeyWithProject)) {
        next.delete(periodKeyWithProject)
      } else {
        next.add(periodKeyWithProject)
      }
      return next
    })
  }, [])

  const loadMoreRuns = useCallback(async (jobId: string) => {
    const pagination = runPagination.get(jobId)
    if (!pagination) return

    const currentRuns = projectHistories.get(jobId)?.runs || []
    const hasMore = currentRuns.length < pagination.total

    if (!hasMore || loadingMoreRuns.has(jobId)) return

    setLoadingMoreRuns(prev => new Set(prev).add(jobId))

    setHistoryErrors(prev => { const next = new Map(prev); next.delete(jobId); return next })
    const newOffset = pagination.offset + pagination.limit
    try {
      const history = await jobProjectApi.getProjectRuns(jobId, {
        limit: RUNS_LOAD_MORE,
        offset: newOffset
      })

      setProjectHistories(prev => {
        const existing = prev.get(jobId)
        if (existing) {
          return new Map(prev).set(jobId, {
            ...existing,
            runs: [...existing.runs, ...history.runs]
          })
        }
        return prev
      })

      setRunPagination(prev => new Map(prev).set(jobId, {
        limit: RUNS_LOAD_MORE,
        offset: newOffset,
        total: pagination.total
      }))
    } catch (error) {
      setHistoryErrors(prev => new Map(prev).set(jobId, 'Impossible de charger les versions suivantes. Réessayez.'))
    } finally {
      setLoadingMoreRuns(prev => {
        const next = new Set(prev)
        next.delete(jobId)
        return next
      })
    }
  }, [runPagination, projectHistories, loadingMoreRuns])

  const toggleRunSelection = useCallback((runId: string) => {
    setSelectedRuns(prev => {
      const next = new Set(prev)
      if (next.has(runId)) {
        next.delete(runId)
      } else {
        next.add(runId)
      }
      return next
    })
  }, [])

  const selectAllRunsInPeriod = useCallback((runs: any[]) => {
    setSelectedRuns(prev => {
      const next = new Set(prev)
      runs.forEach(run => next.add(run.id))
      return next
    })
  }, [])

  const deselectAllRunsInPeriod = useCallback((runs: any[]) => {
    setSelectedRuns(prev => {
      const next = new Set(prev)
      runs.forEach(run => next.delete(run.id))
      return next
    })
  }, [])

  const toggleAllRunsInPeriod = useCallback((runs: any[]) => {
    const allSelected = runs.every(run => selectedRunIds.has(run.id))
    if (allSelected) {
      deselectAllRunsInPeriod(runs)
    } else {
      selectAllRunsInPeriod(runs)
    }
  }, [selectedRunIds, selectAllRunsInPeriod, deselectAllRunsInPeriod])

  const handleBulkDelete = useCallback(async (jobId: string) => {
    if (!canManage) return
    const selectedRuns = projectRunSelection(selectedRunIds, projectHistories.get(jobId)?.runs)
    if (selectedRuns.size === 0) return

    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedRuns.size} version(s) ? Cette action est irréversible.`)) {
      try {
        await Promise.all(
          Array.from(selectedRuns).map(runId => jobProjectApi.deleteRun(jobId, runId))
        )

        // Clear selections and refresh
        setSelectedRuns(prev => new Set([...prev].filter(id => !selectedRuns.has(id))))
        jobProjectApi.getProjectRuns(jobId, { limit: RUNS_INITIAL_LIMIT, offset: 0 }).then(history => {
          setProjectHistories(prev => new Map(prev).set(jobId, history))
          setRunPagination(prev => new Map(prev).set(jobId, {
            limit: RUNS_INITIAL_LIMIT,
            offset: 0,
            total: history.total || 0
          }))
        })
      } catch (error) {
        console.error('Erreur lors de la suppression groupée:', error)
        alert('Erreur lors de la suppression groupée')
      }
    }
  }, [selectedRunIds, projectHistories, canManage])

  const handleDeletePeriodRuns = useCallback(async (jobId: string, runs: any[]) => {
    if (!canManage) return
    if (runs.length === 0) return

    if (window.confirm(`Êtes-vous sûr de vouloir supprimer toutes les ${runs.length} versions de cette période ? Cette action est irréversible.`)) {
      try {
        await Promise.all(
          runs.map(run => jobProjectApi.deleteRun(jobId, run.id))
        )

        // Refresh the project history
        jobProjectApi.getProjectRuns(jobId, { limit: RUNS_INITIAL_LIMIT, offset: 0 }).then(history => {
          setProjectHistories(prev => new Map(prev).set(jobId, history))
          setRunPagination(prev => new Map(prev).set(jobId, {
            limit: RUNS_INITIAL_LIMIT,
            offset: 0,
            total: history.total || 0
          }))
        })
      } catch (error) {
        console.error('Erreur lors de la suppression de la période:', error)
        alert('Erreur lors de la suppression de la période')
      }
    }
  }, [canManage])


  if (!rights.read && !access.isLoading) return <p role="alert">La consultation des optimisations nécessite une autorisation de lecture.</p>

  return (
    <div className="max-w-7xl mx-auto space-y-0">
      {/* ── CARGO HEADER BANNER ── */}
      <div className="cargo-bg rounded-none sm:rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden mb-6">
        {/* Top accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-orange-500 via-amber-400 to-orange-600" />
        <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            {/* Icon cargo container */}
            <div className="relative w-14 h-14 rounded-xl border border-orange-200 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center flex-shrink-0">
              <CubeIcon className="w-7 h-7 text-orange-600 dark:text-orange-400" />
              <div className="absolute -top-1 -right-1 w-4.5 h-4.5 rounded-full bg-orange-500 flex items-center justify-center shadow-sm">
                <span className="text-[8px] font-black text-white">3D</span>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-black text-slate-900 dark:text-white tracking-tight uppercase">Projets de Chargement</h1>
                <span className="text-[10px] font-extrabold px-2 py-0.5 rounded border border-orange-300 dark:border-orange-500/40 bg-orange-50/50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400 uppercase tracking-widest">TMS</span>
              </div>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 font-mono">
                Gérez et visualisez vos projets de chargement
              </p>
            </div>
          </div>
          <Button
            disabled={!canWrite} onClick={() => setCreateProjectDialogOpen(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-700 hover:bg-orange-800 text-white rounded-xl font-semibold text-sm transition-all"
            aria-label="Créer un nouveau projet de chargement"
            data-e2e="btn-new-project"
          >
            <PlusIcon className="w-4 h-4" />
            Nouveau projet
          </Button>
        </div>
      </div>

      <div className="px-0 sm:px-0 space-y-5 warehouse-grid">
        {/* Stats — manifeste style */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: statusFilter || searchQuery ? 'Projets trouvés' : 'Total projets', value: total, icon: FolderIcon, accent: '#f97316', sub: 'selon les filtres' },
            { label: 'Actifs', value: projects.filter((p: JobProject) => p.status === 'active').length, icon: CheckCircleIcon, accent: '#10b981', sub: 'sur cette page' },
            { label: 'Complétés', value: projects.filter((p: JobProject) => p.status === 'completed').length, icon: CubeIcon, accent: '#3b82f6', sub: 'sur cette page' },
            { label: 'Brouillons', value: projects.filter((p: JobProject) => p.status === 'draft').length, icon: ExclamationTriangleIcon, accent: '#f59e0b', sub: 'sur cette page' },
          ].map(s => {
            const Icon = s.icon
            return (
              <div key={s.label} className="relative cargo-corner bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 overflow-hidden">
                <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 19px, currentColor 20px)', backgroundSize: '100% 20px' }} />
                <div className="relative">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.accent + '18', border: `1.5px solid ${s.accent}40` }}>
                      <Icon className="w-4.5 h-4.5" style={{ color: s.accent }} />
                    </div>
                    <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: s.accent }}>■</span>
                  </div>
                  <p className="text-3xl font-black text-slate-900 dark:text-white font-mono" style={{ textShadow: `0 0 20px ${s.accent}30` }}>{s.value}</p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mt-0.5">{s.label}</p>
                  <p className="text-[10px] text-slate-400 dark:text-slate-600">{s.sub}</p>
                </div>
              </div>
            )
          })}
        </div>

        {/* Toolbar */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="search" value={search} onChange={event => setSearch(event.target.value)} aria-label="Rechercher un projet par référence ou nom" maxLength={128} placeholder="Recherche : référence, nom…"
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm font-mono text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-500/40 focus:border-orange-400 transition-all placeholder:text-slate-400" />
            </div>
            <select
              aria-label="Filtrer les projets par statut"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as JobProject['status'] | '')}
              className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-300 focus:ring-2 focus:ring-orange-500/40 transition-all"
              data-e2e="filter-status"
            >
              {STATUS_FILTER_OPTIONS.map(({ value, label }) => (
                <option key={value || 'all'} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <span className="text-[11px] font-mono font-semibold text-slate-400 dark:text-slate-500 self-center whitespace-nowrap uppercase tracking-wider">
              {total > 0 ? `${rangeStart}–${rangeEnd} sur ${total} projet${total > 1 ? 's' : ''}` : '0 projet'}
              {isFetching && !isLoading ? ' (actualisation…)' : ''}
            </span>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
          <div className="relative overflow-x-auto">
            {isFetching && (
              <div className="absolute inset-0 bg-white/70 dark:bg-slate-900/70 backdrop-blur-[2px] z-10 flex items-center justify-center">
                <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-slate-900/80 dark:bg-slate-700/80 shadow-lg">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-400 border-t-transparent"></div>
                  <span className="text-xs font-bold text-white uppercase tracking-wider">Chargement…</span>
                </div>
              </div>
            )}
            {isLoading && (
              <div className="text-center py-12" role="status" aria-live="polite" aria-busy="true">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-20 h-20 cargo-bg rounded-2xl flex items-center justify-center shadow-xl border border-slate-700">
                    <FolderIcon className="w-9 h-9 text-orange-400 animate-pulse" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-widest">Chargement manifeste…</p>
                    <p className="text-xs text-slate-400 mt-1">Récupération des projets</p>
                  </div>
                </div>
              </div>
            )}

            {error != null ? (
              <div role="alert" className="p-8 text-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                    <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    <strong>Erreur lors du chargement:</strong>{' '}
                    {error instanceof Error ? error.message : 'Erreur inconnue'}
                  </p>
                  <button type="button" disabled={isFetching} onClick={() => void refetch()} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-40 dark:border-slate-700 dark:text-slate-200">Réessayer le chargement des projets</button>
                </div>
              </div>
            ) : null}

            {!isLoading && !error && projects.length === 0 && (
              <div className="py-16 text-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800">
                    <FolderIcon className="w-6 h-6" />
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {statusFilter || searchQuery
                      ? 'Aucun projet ne correspond à ce filtre'
                      : 'Créez un projet pour préparer votre premier chargement.'}
                  </p>
                  {statusFilter || searchQuery ? (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => { setStatusFilter(''); setSearch('') }}
                      className="mr-2"
                    >
                      Réinitialiser les filtres
                    </Button>
                  ) : (
                    <Button
                      disabled={!canWrite} onClick={() => setCreateProjectDialogOpen(true)}
                      className="mt-1 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800 dark:bg-amber-500 dark:text-slate-900 dark:hover:bg-amber-400"
                      data-e2e="btn-create-first"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Créer votre premier projet
                    </Button>
                  )}
                </div>
              </div>
            )}

            {!isLoading && !error && projects.length > 0 && (
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/80 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                    <th className="py-3 px-4 text-[11px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 text-left">Référence</th>
                    <th className="py-3 px-4 text-[11px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 text-left">Nom</th>
                    <th className="py-3 px-4 text-[11px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 text-left">Véhicule</th>
                    <th className="py-3 px-4 text-[11px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 text-left">Statut</th>
                    <th className="py-3 px-4 text-[11px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 text-left">Performance</th>
                    <th className="py-3 px-4 text-[11px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 text-left">Créée le</th>
                    <th className="py-3 px-4 text-[11px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project: JobProject) => {
                    const statusLabel = getStatusLabel(project.status)
                    const currentRun = project.current_run
                    const vehicle = currentRun?.input?.vehicle
                    const vehicleType = vehicle?.type
                    const vehicleSubtype = vehicle?.vehicle_subtype
                    const isExpanded = expandedProjects.has(project.job_id)
                    const history = projectHistories.get(project.job_id)
                    const selectedRuns = projectRunSelection(selectedRunIds, history?.runs)

                    const handleProjectClick = () => {
                      if (currentRun) {
                        navigate(`/optimization/${currentRun.id}${canWrite ? '' : '/viewer'}`)
                      } else {
                        navigate(`/optimization/new`, { state: { jobId: project.job_id } })
                      }
                    }

                    const handleToggleHistory = (e: React.MouseEvent) => {
                      e.stopPropagation()
                      toggleProjectExpansion(project.job_id)
                    }

                    const handleRunClick = (runId: string) => {
                      navigate(`/optimization/${runId}${canWrite ? '' : '/viewer'}`)
                    }

                    const getRunStatusLabel = (status: string) => {
                      const labels: Record<string, string> = {
                        draft: 'Brouillon',
                        queued: 'En attente',
                        running: 'En cours',
                        completed: 'Complété',
                        failed: 'Échoué',
                        cancelled: 'Annulé',
                      }
                      return labels[status] || status
                    }

                    return (
                      <React.Fragment key={project.job_id}>
                        <tr className="group border-b border-slate-100 last:border-0 transition-all duration-150 hover:bg-orange-50/30 dark:border-slate-800 dark:hover:bg-slate-800/30 cursor-pointer" onClick={handleProjectClick}>
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-2.5">
                              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border flex-shrink-0 transition-transform group-hover:scale-105" style={{ backgroundColor: '#f97316' + '18', borderColor: '#f97316' + '40', color: '#f97316' }}>
                                <FolderIcon className="w-4 h-4" />
                              </div>
                              <span className="font-bold text-sm text-slate-900 dark:text-white font-mono tracking-wide">{project.reference}</span>
                            </div>
                          </td>
                          <td className="py-3.5 px-4">
                            <button type="button" onClick={event => { event.stopPropagation(); handleProjectClick() }} className="text-left font-semibold text-sm text-slate-800 hover:text-orange-700 focus-visible:ring-2 focus-visible:ring-orange-500 dark:text-slate-200">{project.name}</button><span className="ml-2 text-xs text-slate-500">{project.measurement_system === 'imperial' ? 'Impérial' : 'Métrique'}</span>
                          </td>
                          <td className="py-3.5 px-4">
                            {vehicleType ? (
                              <span className="text-xs text-slate-600 dark:text-slate-400">
                                {getVehicleTypeLabel(vehicleType)}
                                {vehicleSubtype ? ` · ${getVehicleSubtypeLabel(vehicleSubtype)}` : ''}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="py-3.5 px-4">
                            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${listStatusBadgeClass(project.status)}`}>
                              <span className="h-1.5 w-1.5 rounded-full bg-current" />
                              {statusLabel}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 text-sm text-slate-600 dark:text-slate-400">
                            {currentRun?.output ? (
                              <div className="flex flex-col gap-0.5">
                                <span className="flex items-center gap-1.5 text-xs">
                                  <CubeIcon className="w-3.5 h-3.5 text-sky-500" />
                                  <span className="font-semibold text-slate-700 dark:text-slate-300">{currentRun.output.fill_rate_volume == null ? '—' : `${(currentRun.output.fill_rate_volume * 100).toFixed(1)} %`}</span>
                                </span>
                                <span className="flex items-center gap-1.5 text-xs">
                                  <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-500" />
                                  <span className="font-semibold text-slate-700 dark:text-slate-300">{currentRun.output.global_score == null ? '—' : `${(currentRun.output.global_score * 100).toFixed(1)}/100`}</span>
                                </span>
                              </div>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="py-3.5 px-4 text-xs text-slate-600 dark:text-slate-400">
                            {new Date(project.created_at).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="py-3.5 px-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button type="button" disabled={!canWrite} onClick={event => { event.stopPropagation(); setEditingProject(project) }} aria-label={`Modifier le projet ${project.name}`} title="Modifier les informations du projet" className="rounded-lg p-2 text-slate-500 hover:bg-orange-50 hover:text-orange-700 disabled:opacity-40 dark:hover:bg-slate-800"><PencilIcon className="h-4 w-4" /></button>
                              <button
                                type="button"
                                onClick={handleToggleHistory}
                                className="rounded-lg p-2 text-slate-400 transition-all hover:bg-orange-50 hover:text-orange-600 dark:hover:bg-orange-900/20 dark:hover:text-orange-400 opacity-100"
                                aria-expanded={isExpanded}
                                aria-label={`Historique de ${project.name}`}
                                title={isExpanded ? "Masquer l'historique" : "Voir l'historique"}
                              >
                                <ClockIcon className="w-4 h-4" />
                              </button>
                              <button disabled={!canWrite}
                                type="button"
                                onClick={(e) => handleDuplicateProject(project.job_id, project.name, e)}
                                className="rounded-lg p-2 text-slate-400 transition-all hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 opacity-100"
                                title="Dupliquer le projet"
                              >
                                <DocumentDuplicateIcon className="w-4 h-4" />
                              </button>
                              <button disabled={!canManage}
                                type="button"
                                onClick={(e) => handleDeleteProject(project.job_id, e)}
                                className="rounded-lg p-2 text-slate-400 transition-all hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 opacity-100"
                                title="Supprimer le projet"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                              <ChevronRightIcon className="w-5 h-5 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                            </div>
                          </td>
                        </tr>
                        {/* History sublist */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className="py-3 px-4 bg-slate-50/50 dark:bg-slate-800/30">
                              <div className="ml-4 space-y-4">
                                {historyErrors.has(project.job_id) && <div role="alert" className="flex items-center gap-3 text-sm text-red-700 dark:text-red-300">{historyErrors.get(project.job_id)}<button type="button" disabled={loadingMoreRuns.has(project.job_id)} onClick={() => void loadProjectHistory(project.job_id)} className="rounded-lg border border-current px-3 py-2">Réessayer</button></div>}
                                {loadingMoreRuns.has(project.job_id) && !history && <p role="status" className="text-sm text-slate-500">Chargement de l’historique…</p>}
                                {history && history.runs.length > 0 ? (
                                  groupRunsByPeriod(history.runs).map(([periodKey, runs, hourGroups]) => {
                                    const periodKeyWithProject = `${project.job_id}-${periodKey}`
                                    const isCollapsed = collapsedPeriods.has(periodKeyWithProject)
                                    const hasHourGroups = hourGroups !== null
                                    const allSelectedInPeriod = runs.every(run => selectedRuns.has(run.id))
                                    const someSelectedInPeriod = runs.some(run => selectedRuns.has(run.id))

                                    return (
                                      <div key={periodKeyWithProject} className="space-y-2">
                                        <div
                                          className="flex items-center gap-2 mb-3 cursor-pointer group"
                                          onClick={() => togglePeriodCollapse(periodKeyWithProject)}
                                        >
                                          <div className="h-px flex-1 bg-gradient-to-r from-orange-200 to-transparent dark:from-orange-800/50 dark:to-transparent" />
                                          <div className="flex items-center gap-2 px-3 py-1 bg-orange-50 dark:bg-orange-950/30 rounded-full border border-orange-200 dark:border-orange-800/50 hover:bg-orange-100 dark:hover:bg-orange-950/50 transition-colors">
                                            <span className="text-xs font-bold text-orange-700 dark:text-orange-400 uppercase tracking-wider">
                                              {runs[0].periodLabel}
                                            </span>
                                            <span className="text-xs font-semibold text-orange-600 dark:text-orange-500">
                                              ({runs.length})
                                            </span>
                                            {isCollapsed ? (
                                              <ChevronDownIcon className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                                            ) : (
                                              <ChevronUpIcon className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                                            )}
                                          </div>
                                          {!isCollapsed && (
                                            <div className="flex items-center gap-1">
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation()
                                                  toggleAllRunsInPeriod(runs)
                                                }}
                                                className={`rounded-full p-1.5 transition-all ${allSelectedInPeriod
                                                  ? 'bg-orange-500 text-white'
                                                  : someSelectedInPeriod
                                                    ? 'bg-orange-200 text-orange-700 dark:bg-orange-800 dark:text-orange-300'
                                                    : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-orange-100 dark:hover:bg-orange-900/30'
                                                  }`}
                                                title={allSelectedInPeriod ? "Désélectionner tout" : "Sélectionner tout"}
                                              >
                                                <CheckIcon className="w-3 h-3" />
                                              </button>
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation()
                                                  canManage && handleDeletePeriodRuns(project.job_id, runs)
                                                }}
                                                className="rounded-full p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-all"
                                                title="Supprimer toutes les versions de cette période"
                                                disabled={!canManage}
                                              >
                                                <TrashIcon className="w-3 h-3" />
                                              </button>
                                            </div>
                                          )}
                                          <div className="h-px flex-1 bg-gradient-to-l from-orange-200 to-transparent dark:from-orange-800/50 dark:to-transparent" />
                                        </div>
                                        {!isCollapsed && (
                                          <div className="space-y-2">
                                            {hasHourGroups ? (
                                              // Display hour groups
                                              hourGroups.map(([hourKey, hourRuns]) => {
                                                const hourKeyWithProject = `${project.job_id}-${hourKey}`
                                                const isHourCollapsed = collapsedPeriods.has(hourKeyWithProject)
                                                const allSelectedInHour = hourRuns.every(run => selectedRuns.has(run.id))
                                                const someSelectedInHour = hourRuns.some(run => selectedRuns.has(run.id))

                                                return (
                                                  <div key={hourKeyWithProject} className="space-y-2">
                                                    <div
                                                      className="flex items-center gap-2 mb-2 cursor-pointer group"
                                                      onClick={() => togglePeriodCollapse(hourKeyWithProject)}
                                                    >
                                                      <div className="h-px flex-1 bg-gradient-to-r from-amber-200 to-transparent dark:from-amber-800/50 dark:to-transparent" />
                                                      <div className="flex items-center gap-2 px-2 py-0.5 bg-amber-50 dark:bg-amber-950/30 rounded-full border border-amber-200 dark:border-amber-800/50 hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors">
                                                        <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider">
                                                          {hourRuns[0].hourLabel}
                                                        </span>
                                                        <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-500">
                                                          ({hourRuns.length})
                                                        </span>
                                                        {isHourCollapsed ? (
                                                          <ChevronDownIcon className="w-2.5 h-2.5 text-amber-600 dark:text-amber-400" />
                                                        ) : (
                                                          <ChevronUpIcon className="w-2.5 h-2.5 text-amber-600 dark:text-amber-400" />
                                                        )}
                                                      </div>
                                                      {!isHourCollapsed && (
                                                        <div className="flex items-center gap-1">
                                                          <button
                                                            type="button"
                                                            onClick={(e) => {
                                                              e.stopPropagation()
                                                              toggleAllRunsInPeriod(hourRuns)
                                                            }}
                                                            className={`rounded-full p-1 transition-all ${allSelectedInHour
                                                              ? 'bg-amber-500 text-white'
                                                              : someSelectedInHour
                                                                ? 'bg-amber-200 text-amber-700 dark:bg-amber-800 dark:text-amber-300'
                                                                : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-amber-100 dark:hover:bg-amber-900/30'
                                                              }`}
                                                            title={allSelectedInHour ? "Désélectionner tout" : "Sélectionner tout"}
                                                          >
                                                            <CheckIcon className="w-2.5 h-2.5" />
                                                          </button>
                                                          <button
                                                            type="button"
                                                            onClick={(e) => {
                                                              e.stopPropagation()
                                                              canManage && handleDeletePeriodRuns(project.job_id, hourRuns)
                                                            }}
                                                            className="rounded-full p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-all"
                                                            title="Supprimer toutes les versions de cette heure"
                                                            disabled={!canManage}
                                                          >
                                                            <TrashIcon className="w-2.5 h-2.5" />
                                                          </button>
                                                        </div>
                                                      )}
                                                      <div className="h-px flex-1 bg-gradient-to-l from-amber-200 to-transparent dark:from-amber-800/50 dark:to-transparent" />
                                                    </div>
                                                    {!isHourCollapsed && (
                                                      <div className="space-y-2">
                                                        {hourRuns.map((run) => (
                                                          <div
                                                            key={run.id}
                                                            className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer shadow-sm ${selectedRuns.has(run.id)
                                                              ? 'bg-orange-50 border-orange-300 dark:bg-orange-950/30 dark:border-orange-700'
                                                              : 'bg-white border-slate-200 dark:bg-slate-900 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'
                                                              }`}
                                                            onClick={(e) => {
                                                              e.stopPropagation()
                                                              handleRunClick(run.id)
                                                            }}
                                                          >
                                                            <div className="flex items-center gap-3">
                                                              <div
                                                                role="checkbox" tabIndex={0} aria-checked={selectedRuns.has(run.id)} aria-label={`Sélectionner la version ${run.plan_version} du projet ${project.name}`}
                                                                onKeyDown={event => { if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); toggleRunSelection(run.id) } }}
                                                                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${selectedRuns.has(run.id)
                                                                  ? 'bg-orange-500 border-orange-500'
                                                                  : 'border-slate-300 dark:border-slate-600 hover:border-orange-400'
                                                                  }`}
                                                                onClick={(e) => {
                                                                  e.stopPropagation()
                                                                  toggleRunSelection(run.id)
                                                                }}
                                                              >
                                                                {selectedRuns.has(run.id) && (
                                                                  <CheckIcon className="w-3 h-3 text-white" />
                                                                )}
                                                              </div>
                                                              <div className="w-2 h-2 rounded-full bg-orange-500 shadow-sm shadow-orange-500/50" />
                                                              <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                  <span className="text-sm font-bold text-slate-900 dark:text-white">
                                                                    Version {run.plan_version} · {run.input?.measurement_system === 'imperial' ? 'Impérial' : 'Métrique'}
                                                                  </span>
                                                                  <span
                                                                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${listStatusBadgeClass(run.status)}`}
                                                                  >
                                                                    {getRunStatusLabel(run.status)}
                                                                  </span>
                                                                </div>
                                                                <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                                                                  {new Date(run.requested_at).toLocaleDateString('fr-FR', {
                                                                    day: 'numeric',
                                                                    month: 'short',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit',
                                                                  })}
                                                                </p>
                                                                <p className="mt-1 text-xs font-medium text-blue-700 dark:text-blue-300">
                                                                  {run.publication
                                                                    ? <>Diffusion : {run.publication.status} · {run.publication.artifact_count} artefact(s){run.publication.published_at ? ` · ${new Date(run.publication.published_at).toLocaleDateString('fr-FR')}` : ''}</>
                                                                    : <>Diffusion : non demandée · 0 artefact</>}
                                                                </p>
                                                              </div>
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                              <div className="text-right">
                                                                {run.output && (
                                                                  <div className="text-xs">
                                                                    <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                                                                      <CubeIcon className="w-3 h-3 text-sky-500" />
                                                                      <span className="font-semibold">{run.output.fill_rate_volume == null ? '—' : `${(run.output.fill_rate_volume * 100).toFixed(1)} %`}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                                                                      <CheckCircleIcon className="w-3 h-3 text-emerald-500" />
                                                                      <span className="font-semibold">{run.output.global_score == null ? '—' : `${(run.output.global_score * 100).toFixed(1)}/100`}</span>
                                                                    </div>
                                                                  </div>
                                                                )}
                                                              </div>
                                                              <button disabled={!canManage}
                                                                type="button"
                                                                onClick={(e) => handleDeleteRun(project.job_id, run.id, e)}
                                                                className="rounded-lg p-2 text-slate-400 transition-all hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                                                                title="Supprimer cette version"
                                                              >
                                                                <TrashIcon className="w-4 h-4" />
                                                              </button>
                                                            </div>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                )
                                              })
                                            ) : (
                                              // Display regular runs
                                              runs.map((run) => (
                                                <div
                                                  key={run.id}
                                                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer shadow-sm ${selectedRuns.has(run.id)
                                                    ? 'bg-orange-50 border-orange-300 dark:bg-orange-950/30 dark:border-orange-700'
                                                    : 'bg-white border-slate-200 dark:bg-slate-900 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'
                                                    }`}
                                                  onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleRunClick(run.id)
                                                  }}
                                                >
                                                  <div className="flex items-center gap-3">
                                                    <div
                                                      role="checkbox" tabIndex={0} aria-checked={selectedRuns.has(run.id)} aria-label={`Sélectionner la version ${run.plan_version} du projet ${project.name}`}
                                                      onKeyDown={event => { if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); toggleRunSelection(run.id) } }}
                                                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${selectedRuns.has(run.id)
                                                        ? 'bg-orange-500 border-orange-500'
                                                        : 'border-slate-300 dark:border-slate-600 hover:border-orange-400'
                                                        }`}
                                                      onClick={(e) => {
                                                        e.stopPropagation()
                                                        toggleRunSelection(run.id)
                                                      }}
                                                    >
                                                      {selectedRuns.has(run.id) && (
                                                        <CheckIcon className="w-3 h-3 text-white" />
                                                      )}
                                                    </div>
                                                    <div className="w-2 h-2 rounded-full bg-orange-500 shadow-sm shadow-orange-500/50" />
                                                    <div>
                                                      <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-sm font-bold text-slate-900 dark:text-white">
                                                          Version {run.plan_version} · {run.input?.measurement_system === 'imperial' ? 'Impérial' : 'Métrique'}
                                                        </span>
                                                        <span
                                                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${listStatusBadgeClass(run.status)}`}
                                                        >
                                                          {getRunStatusLabel(run.status)}
                                                        </span>
                                                      </div>
                                                      <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                                                        {new Date(run.requested_at).toLocaleDateString('fr-FR', {
                                                          day: 'numeric',
                                                          month: 'short',
                                                          hour: '2-digit',
                                                          minute: '2-digit',
                                                        })}
                                                      </p>
                                                      <p className="mt-1 text-xs font-medium text-blue-700 dark:text-blue-300">
                                                        {run.publication
                                                          ? <>Diffusion : {run.publication.status} · {run.publication.artifact_count} artefact(s){run.publication.published_at ? ` · ${new Date(run.publication.published_at).toLocaleDateString('fr-FR')}` : ''}</>
                                                          : <>Diffusion : non demandée · 0 artefact</>}
                                                      </p>
                                                    </div>
                                                  </div>
                                                  <div className="flex items-center gap-3">
                                                    <div className="text-right">
                                                      {run.output && (
                                                        <div className="text-xs">
                                                          <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                                                            <CubeIcon className="w-3 h-3 text-sky-500" />
                                                            <span className="font-semibold">{run.output.fill_rate_volume == null ? '—' : `${(run.output.fill_rate_volume * 100).toFixed(1)} %`}</span>
                                                          </div>
                                                          <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                                                            <CheckCircleIcon className="w-3 h-3 text-emerald-500" />
                                                            <span className="font-semibold">{run.output.global_score == null ? '—' : `${(run.output.global_score * 100).toFixed(1)}/100`}</span>
                                                          </div>
                                                        </div>
                                                      )}
                                                    </div>
                                                    <button disabled={!canManage}
                                                      type="button"
                                                      onClick={(e) => handleDeleteRun(project.job_id, run.id, e)}
                                                      className="rounded-lg p-2 text-slate-400 transition-all hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                                                      title="Supprimer cette version"
                                                    >
                                                      <TrashIcon className="w-4 h-4" />
                                                    </button>
                                                  </div>
                                                </div>
                                              ))
                                            )}
                                          </div>
                                        )}
                                        {/* Load more button */}
                                        {(() => {
                                          const pagination = runPagination.get(project.job_id)
                                          const currentRuns = history?.runs.length || 0
                                          const hasMore = pagination && currentRuns < pagination.total
                                          const isLoading = loadingMoreRuns.has(project.job_id)

                                          if (hasMore) {
                                            return (
                                              <div className="mt-3 text-center">
                                                <button
                                                  type="button"
                                                  onClick={() => loadMoreRuns(project.job_id)}
                                                  disabled={isLoading}
                                                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                  {isLoading ? (
                                                    <>
                                                      <div className="animate-spin rounded-full h-3 w-3 border-2 border-slate-600 border-t-transparent" />
                                                      Chargement...
                                                    </>
                                                  ) : (
                                                    <>
                                                      Charger plus de versions
                                                      <span className="text-slate-500 dark:text-slate-400">
                                                        ({pagination.total - currentRuns} restantes)
                                                      </span>
                                                    </>
                                                  )}
                                                </button>
                                              </div>
                                            )
                                          }
                                          return null
                                        })()}

                                        {/* Bulk delete action */}
                                        {selectedRuns.size > 0 && (
                                          <div className="mt-3 flex items-center justify-center gap-3 p-3 bg-orange-50 dark:bg-orange-950/30 rounded-lg border border-orange-200 dark:border-orange-800/50">
                                            <span className="text-xs font-semibold text-orange-700 dark:text-orange-400">
                                              {selectedRuns.size} version(s) sélectionnée(s)
                                            </span>
                                            <button
                                              type="button"
                                              disabled={!canManage} onClick={() => handleBulkDelete(project.job_id)}
                                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-semibold transition-colors"
                                            >
                                              <TrashIcon className="w-3.5 h-3.5" />
                                              Supprimer
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => setSelectedRuns(prev => new Set([...prev].filter(id => !selectedRuns.has(id))))}
                                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-semibold transition-colors"
                                            >
                                              ✕ Annuler
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    )
                                  })
                                ) : (
                                  <p className="text-sm text-slate-500 dark:text-slate-400 italic">
                                    {!loadingMoreRuns.has(project.job_id) && !historyErrors.has(project.job_id) ? 'Aucune optimisation pour ce projet' : ''}
                                  </p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            )}

            {totalPages > 1 && (
              <div className="flex flex-col gap-3 border-t border-slate-100 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between px-4 py-3">
                <p className="text-xs font-mono font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Page <span className="text-slate-900 dark:text-white">{page + 1}</span> / {totalPages}
                  <span className="mx-2 text-slate-300 dark:text-slate-700">·</span>
                  <span className="text-slate-900 dark:text-white">{total}</span> projet{total !== 1 ? 's' : ''}
                </p>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0 || isFetching}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 transition-all hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Page précédente"
                    data-e2e="pagination-prev"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                    Précédent
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1 || isFetching}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 transition-all hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Page suivante"
                    data-e2e="pagination-next"
                  >
                    Suivant
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {editingProject && canWrite && <CreateProjectDialog project={editingProject} onClose={() => setEditingProject(null)} onSaved={() => void refetch()} />}
      {createProjectDialogOpen && canWrite && (
        <CreateProjectDialog onClose={() => setCreateProjectDialogOpen(false)} onSaved={() => void refetch()} />
      )}
    </div>
  )
}
